import React from 'react'
import { Box, Typography } from '@mui/material'
import { ExternalGigproLinks } from '../../constants'
import { useForwardedHref } from '../../utils/appUrl'
import { MonoButton } from './components'

export type ConfirmSuccess = {
  locationId: number
  phoneE164: string
  date: string
}

const buildBizLoginUrl = ({
  locationId,
  phoneE164,
  date,
}: ConfirmSuccess): string => {
  const base = ExternalGigproLinks.login.replace(/\/$/, '')
  const returnTo = `/locations/${locationId}/schedule?calendar_date=${date}`
  return `${base}/auth/phone?phone=${encodeURIComponent(
    phoneE164,
  )}&returnTo=${encodeURIComponent(returnTo)}`
}

// Seal badge + check, ported from qwickgo's <Sticker icon={check_circle} />.
const SuccessSeal: React.FC = () => (
  <Box sx={{ position: 'relative', width: 64, height: 64, mb: 0.5 }}>
    <Box
      component="svg"
      viewBox="0 0 80 80"
      sx={{
        position: 'absolute',
        inset: 0,
        width: 64,
        height: 64,
        color: 'primary.main',
      }}
    >
      <path
        d="M40 4 L46 14 L58 10 L58 22 L70 24 L66 36 L76 42 L68 52 L72 64 L60 66 L56 78 L44 72 L40 80 L36 72 L24 78 L20 66 L8 64 L12 52 L4 42 L14 36 L10 24 L22 22 L22 10 L34 14 Z"
        fill="currentColor"
      />
    </Box>
    <Box
      component="svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      sx={{
        position: 'absolute',
        inset: 0,
        m: 'auto',
        width: 30,
        height: 30,
        color: 'common.black',
      }}
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="7,12 10,15 17,9" />
    </Box>
  </Box>
)

type Props = {
  confirmed: ConfirmSuccess
  onClose: () => void
}

export const CheckoutSuccess: React.FC<Props> = ({ confirmed, onClose }) => {
  const href = useForwardedHref(buildBizLoginUrl(confirmed))
  // Guard against accidental tab close/refresh; the dashboard button opts out.
  const leavingViaButton = React.useRef(false)
  React.useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (leavingViaButton.current) return
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: 1.5,
        p: '48px 40px',
        minHeight: 380,
        justifyContent: 'center',
      }}
    >
      <SuccessSeal />
      <Typography
        variant="h3"
        component="h2"
        sx={{ fontSize: 22, fontWeight: 900, textTransform: 'uppercase' }}
      >
        You're all set
      </Typography>
      <Typography
        sx={{
          fontSize: 14,
          color: 'neutral.gray',
          lineHeight: 1.7,
          maxWidth: 420,
        }}
      >
        Your shift request has been received. Head to your Qwick dashboard to
        track your match, manage your upcoming shift, and view your pro once
        confirmed. Log in with the phone number you just used to get started.
      </Typography>
      <MonoButton
        variant="contained"
        color="primary"
        href={href}
        onClick={() => {
          leavingViaButton.current = true
          onClose()
        }}
        sx={{ mt: 2, px: 3, fontWeight: 700 }}
      >
        Go to my dashboard →
      </MonoButton>
    </Box>
  )
}
